import React, {
	FC, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
	AppEnum,
	ColumnPicker,
	useUrlParams,
} from '@aleph/shared-components';
import {
	columnEditorPlugin,
	dashboardViewPlugin,
	elasticPropertyPlugin,
	nameSizingPlugin,
	SuperGridAnchors,
	useSuperGridEngine,
	useSuperGridPlugin,
	validatePlugin,
} from '@aleph/supergrid';
import { useApolloClient } from '@apollo/client';
import {
	getRedditSuperGridRowClassRules,
} from 'wb-reddit/app/routes/superGrid/view/utils';
import LoadingCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/loading';
import SpotifyActionRow from 'wb-spotify/app/routes/superGrid/actionRow/container';
import { debounce, isEqual } from 'lodash';
import {
	MAPPED_COLUMN_NAMES,
	SPOTIFY_ALWAYS_VISIBLE_COLUMNS,
	SPOTIFY_SUPERGRID_ICONS,
	SpotifyBreakdowns,
	RedditSuperGridContext,
	processValueFromJSON,
	processValueToJSON,
	SpotifyLevel,
	getDateRangeForSupergridFromParams,
} from 'wb-spotify/app/routes/superGrid/utils';
import NoRowsOverlay from 'wb-reddit/app/routes/superGrid/view/noRowsOverlay';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import {
	GridApi,
	ColumnApi,
	GridReadyEvent,
	RowSelectedEvent,
	SortChangedEvent,
	RangeSelectionChangedEvent,
	FirstDataRenderedEvent,
	CellValueChangedEvent,
	CellClickedEvent,
	BodyScrollEvent,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import useInstantEditPlugin from 'wb-reddit/app/routes/superGrid/hooks/useInstantEditPlugin';
import SpotifySupergridBulkActions from 'wb-spotify/app/routes/superGrid/actionRow/bulkActions/container';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import { DEFAULT_SPOTIFY_COL_DEF, mapSortField, filterSortField } from 'wb-spotify/app/routes/superGrid/view/utils';
import SpotifySuperGridBreakdownSelector from 'wb-spotify/app/routes/superGrid/tool/breakdownSelector';
import {
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';
import ToggleLifetimeStats from 'wb-spotify/app/routes/superGrid/tool/toggleLifetimestats';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import SpotifyAdvertiserOAuth from 'wb-spotify/app/routes/superGrid/tool/advertiserOAuth/container';
import { useNavigate, useParams } from 'react-router-dom';
import AddNewSpotifyEntityButton from 'wb-spotify/app/routes/superGrid/mutator/add/button';
import useSpotifyEditPlugin from 'wb-spotify/app/routes/superGrid/view/hooks/useSpotifyEditPlugin';
import { ValidationNavigator } from 'wb-spotify/app/common/supergridValidation';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

LicenseManager.setLicenseKey(
	// eslint-disable-next-line max-len
	'Using_this_AG_Grid_Enterprise_key_( AG-043155 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Httpool d.o.o. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 1 July 2024 )____[v2]_MTcxOTc4ODQwMDAwMA==3f4d73dbd0b55114fae1030bca4daf44',
);

function getSelectedMetrics(lifetimeStats: boolean) {
	return lifetimeStats
		? SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED[0].metrics.map(metric => metric.value)
		: SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED[0].metrics.map(metric => metric.value);
}

const SpotifyViewSuperGrid: FC = () => {
	const { lifetimeStats } = useSupergridSettings();
	const redditSuperGridContext = useContext(RedditSuperGridContext);
	const [selectedMetrics, setSelectedMetrics] = useState<string[]>(getSelectedMetrics(lifetimeStats));
	const [api, setApi] = useState<GridApi | null>(null);
	const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
	const navigate = useNavigate();
	const { params, setUrlParam } = useUrlParams({ navigate, useParams });
	const platformUser = useUserMetaData(AppEnum.SPOTIFY);

	useEffect(() => {
		setSelectedMetrics(getSelectedMetrics(lifetimeStats));
	}, [lifetimeStats]);

	useEffect(() => {
		if (api) {
			api.refreshServerSide({ purge: true });
		}
	}, [lifetimeStats, api]);

	const onGridReady = (e: GridReadyEvent) => {
		setApi(e.api);
		setColumnApi(e.columnApi);
		redditSuperGridContext.setGridApi(e.api);
		e.api.autoSizeColumns(['groupTargets.groupTargetsArtistIds']);
	};

	const client = useApolloClient();
	const gridBodyElement = document.querySelector('#super-grid .ag-center-cols-viewport');

	const [
		selectedBreakdown,
		setSelectedBreakdownType,
	] = useState<SpotifyBreakdowns | undefined>(SpotifyBreakdowns.ENTITIES);

	const hiddenLevels = useMemo(() => {
		if (selectedBreakdown === SpotifyBreakdowns.ENTITIES) {
			return [SpotifyLevel.BREAKDOWN];
		}

		return Object.values(SpotifyLevel).filter(
			level => ![params?.level, SpotifyLevel.BREAKDOWN].includes(level),
		);
	}, [selectedBreakdown, params?.level]);

	const getVariables = useMemo(() => (useBreakdown: boolean) => (level: SpotifyLevel) => {
		const filtersFinal = { ...params.filters ?? {} };
		const date = getDateRangeForSupergridFromParams(params);

		return {
			orderBy: [
				...params.sort?.filter(sortField => filterSortField(mapSortField(sortField.colId, level) as string, level))?.map(sortEntity => ({
					column:    mapSortField(sortEntity.colId, level),
					direction: sortEntity.sort?.toUpperCase(),
				})) ?? {},
			],
			filters: {
				...filtersFinal,
				...(useBreakdown ? { date: { value: date } } : {}),
			},
			statsFilter: { date: { value: date } },
		};
	}, [
		JSON.stringify(params?.filters ?? {}),
		JSON.stringify(params?.date ?? {}),
		JSON.stringify(params?.sort ?? {}),
	]);

	const debouncedRefresh = useMemo(() => debounce(() => api?.refreshCells({ force: true }), 100), [api]);

	const callbacks = useMemo(() => ({
		beforeSuccess: (beforeSuccessParams) => {
			if (
				beforeSuccessParams.items?.length === 0
				&& !beforeSuccessParams.wasCreateAdded
				&& (api?.getRenderedNodes()?.filter(node => !!node?.data)?.length ?? 0) === 0
			) {
				api?.showNoRowsOverlay();
			} else {
				api?.hideOverlay();
			}
		},
	}), [api, selectedBreakdown, JSON.stringify(params?.date ?? {})]);

	const resolverContext = useMemo(() => (
		{
			breakdown: selectedBreakdown,
			client,
			getVariables,
		}
	), [selectedBreakdown, client, getVariables]);

	const {
		props: {
			context: superGridContext,
			onSortChanged: supergridOnSortChanged,
			onBodyScroll,
			...supergridProps
		},
		deepestVisibleLevel,
		updateDeepestVisibleLevel,
		navigateToColumn,
		setElasticProperty,
	} = useSuperGridEngine({
		callbacks,
		gridBodyElement,
		gridApi:      api,
		gridTopLevel: params?.level ?? SpotifyLevel.AD_ACCOUNT,
		config:       SPOTIFY_SUPERGRID_CONFIG,
		plugins:      [
			columnEditorPlugin,
			useSuperGridPlugin(dashboardViewPlugin, {
				configPerRowLevel: [{ level: SpotifyLevel.AD_ACCOUNT }],
			}),
			validatePlugin,
			useSpotifyEditPlugin,
			elasticPropertyPlugin,
			useSuperGridPlugin(nameSizingPlugin, {
				lastNameColId: 'breakdownName',
			}),
			useInstantEditPlugin,
		],
		hiddenLevels,
		context:        resolverContext,
		visibleColumns: {
			GLOBAL: [
				...SPOTIFY_ALWAYS_VISIBLE_COLUMNS,
				...selectedMetrics,
				selectedBreakdown !== SpotifyBreakdowns.ENTITIES
					? params.level === SpotifyLevel.AD_ACCOUNT
						? 'settings'
						: ''
					: 'settings',
				params.level === SpotifyLevel.AD_ACCOUNT ? 'clientPinnedSettings' : '',
			],
		},
	});

	const isEntityOrDailyBreakdown = useMemo(() => (
		(selectedBreakdown === SpotifyBreakdowns.ENTITIES || selectedBreakdown === SpotifyBreakdowns.DAILY)
		&& !lifetimeStats
	), [selectedBreakdown, lifetimeStats]);

	useEffect(() => {
		setElasticProperty(undefined);

		const metricsSource = isEntityOrDailyBreakdown
			? SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED
			: SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED;

		setSelectedMetrics(metricsSource[0].metrics.map(metric => metric.value));
	}, [isEntityOrDailyBreakdown]);

	const superGridRowClassRules = useMemo(
		() => getRedditSuperGridRowClassRules(),
		[],
	);

	useEffect(() => {
		if (api) {
			api?.refreshHeader();
			api?.deselectAll();
			updateDeepestVisibleLevel(api);
		}
	}, [params?.level]);

	useEffect(() => {
		columnApi?.applyColumnState({
			state:        params?.sort,
			defaultState: { sort: null },
		});
	}, [params?.sort]);

	const onRowSelectionChanged = (e: RowSelectedEvent) => {
		e.api.refreshCells({ force: true });
	};

	const onColumnResized = () => {
		debouncedRefresh();
	};

	const onSortChanged = (e: SortChangedEvent) => {
		supergridOnSortChanged?.(e);

		const gridSort = e.columnApi.getColumnState().filter(col => !!col.sort).map((col) => ({
			colId: col.colId,
			sort:  col.sort,
		}));

		e.api?.refreshHeader();

		if (!isEqual(gridSort, params.sort)) {
			setUrlParam(
				'sort',
				gridSort,
			);
		}
	};

	const onRangeSelectionChanged = (e: RangeSelectionChangedEvent) => {
		e.api.refreshCells({ force: true });
	};

	const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
		if (params?.goToColumn?.col) {
			setElasticProperty({
				levels: [params?.level as SpotifyLevel],
				name:   'settingsElastic',
				field:  'settings',
			});

			setTimeout(() => {
				navigateToColumn(params?.goToColumn?.col);
				e.api?.addCellRange({
					rowStartIndex: 0,
					rowEndIndex:   0,
					columnStart:   params?.goToColumn?.col,
					columnEnd:     params?.goToColumn?.col,
				});

				if (params?.goToColumn?.newValue) {
					setTimeout(() => {
						api?.getDisplayedRowAtIndex(0)?.setDataValue(
							params?.goToColumn?.col,
							params?.goToColumn?.newValue,
						);
						api?.flashCells({
							columns:    [params?.goToColumn?.col],
							flashDelay: 200,
						});
						setTimeout(() => {
							setUrlParam('goToColumn', undefined);
						});
					}, 1000);
				} else {
					setUrlParam('goToColumn', undefined);
				}
			}, 500);
		}
	};

	const onCellValueChanged = (e: CellValueChangedEvent) => {
		supergridProps?.onCellValueChanged?.(e);
	};

	const gridWidth = document.getElementById('reddit-super-grid-anchors')?.clientWidth ?? 0;
	// this is the offset of the breakdown button from the right side of the grid
	const breakdownButtonOffset = 110;

	const isServerSideGroupOpenByDefault = (openParams) => {
		const shouldExpand = (params.autoExpand ?? []).includes(openParams?.data?.id);

		if (shouldExpand) {
			setUrlParam(
				'autoExpand',
				params.autoExpand?.filter(id => id !== openParams?.data?.id),
			);
		}

		return shouldExpand
				&& openParams.data?.level !== SpotifyLevel.AD
				&& !openParams.data?.isCreate;
	};

	const columnDefs = useMemo(() => {
		if (platformUser?.readOnly) {
			return (supergridProps?.columnDefs ?? []).map(colDef => ({
				...colDef,
				editable: false,
			}));
		}

		return supergridProps.columnDefs;
	}, [supergridProps.columnDefs]);

	const onCellClicked = (e: CellClickedEvent) => {
		if (e.data?.level !== SpotifyLevel.AD_ACCOUNT) {
			supergridProps?.onCellClicked?.(e);
		}
	};

	const onScroll = useCallback((e: BodyScrollEvent) => {
		const columnEditor = document.getElementById('column-editor-wrapper');

		if (!columnEditor) {
			api?.stopEditing(true);
		}

		// This is all of the columnsthat are visible, even the ones not loaded by ag grid virtual
		const displayedColumns = e.api.getAllDisplayedColumns().filter(col => !col.isPinnedLeft());
		// This is the current columns that are loaded by ag grid virtual
		const virtualColumns = e.api.getAllDisplayedVirtualColumns().filter(col => !col.isPinnedLeft());

		// If displayed columns and virtual columns are the same,
		// it means all columns are loaded, we dont want name resizing to happen
		localStorage.setItem(
			'debug_disableNameSizing',
			`${displayedColumns.length === virtualColumns.length && e.left > 0 ? 1 : 0}`,
		);

		onBodyScroll?.(e);
	}, [onBodyScroll]);

	const { canCreateAdAccount } = useSpotifyRolePermissions();

	return (
		<div className="flex-1 flex flex-col min-w-0">
			<ValidationNavigator gridApi={api}
				mappedFields={MAPPED_COLUMN_NAMES}
				initialHidden
			/>

			<SpotifyActionRow gridApi={api} />

			<div className="relative">
				<SpotifySupergridBulkActions gridApi={api} />

				<div className="flex justify-between items-center gap-[8px]">
					<div id="reddit-super-grid-anchors"
						className="relative flex-1 whitespace-nowrap overflow-hidden"
					>
						<SuperGridAnchors
							gridTopLevel={params?.level ?? SpotifyLevel.AD_ACCOUNT}
							gridBodyElement={gridBodyElement}
							gridWidth={gridWidth - breakdownButtonOffset}
							config={SPOTIFY_SUPERGRID_CONFIG}
							minWidth={36}
							gridApi={api}
							deepestVisibleLevel={deepestVisibleLevel}
							leftBoundary="expand"
							pinnedRightWidth={50}
							pinLeft={true}
						>
							<div className="absolute h-full pl-[1px] flex items-center">
								{params.level === SpotifyLevel.AD_ACCOUNT ? (
									canCreateAdAccount ? (
										<SpotifyAdvertiserOAuth />
									) : null
								) : (
									<AddNewSpotifyEntityButton />
								)}
							</div>
						</SuperGridAnchors>
					</div>

					<SpotifySuperGridBreakdownSelector
						selectedBreakdown={selectedBreakdown}
						setSelectedBreakdownType={setSelectedBreakdownType}
						level={params.level}
					/>

					<ColumnPicker
						onConfirm={setSelectedMetrics}
						selectedMetrics={selectedMetrics}
						metricCategories={
							isEntityOrDailyBreakdown
								? SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED
								: SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED
						}
					/>

					{/* Lifetime stats toggle should only be visible for entities breakdown */}
					{selectedBreakdown === SpotifyBreakdowns.ENTITIES && <ToggleLifetimeStats />}
				</div>
			</div>

			<div id="super-grid"
				className="ag-theme-alpine flex-1 mr-[-40px] mb-[-11px]"
			>
				<AgGridReact
					{...supergridProps}
					blockLoadDebounceMillis={100}
					columnDefs={columnDefs}
					key={`${params?.level}_${JSON.stringify(params?.filters)}_${selectedBreakdown}`}
					processCellFromClipboard={processValueFromJSON}
					processCellForClipboard={processValueToJSON}
					onFirstDataRendered={onFirstDataRendered}
					animateRows
					autoSizePadding={0}
					rowModelType="serverSide"
					onGridReady={onGridReady}
					onBodyScroll={onScroll}
					maxConcurrentDatasourceRequests={1}
					rowSelection="multiple"
					rowBuffer={Number(localStorage.getItem('debug_rowBuffer') ?? 10)}
					cacheBlockSize={Number(localStorage.getItem('debug_cacheBlockSize') ?? 100)}
					headerHeight={36}
					rowHeight={144}
					suppressContextMenu
					enableRangeSelection
					enableFillHandle
					suppressRowClickSelection
					enableGroupEdit
					suppressPropertyNamesCheck
					enableRangeHandle
					serverSideSortAllLevels
					purgeClosedRowNodes
					isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
					noRowsOverlayComponent={NoRowsOverlay}
					onSortChanged={onSortChanged}
					onColumnResized={onColumnResized}
					onCellValueChanged={onCellValueChanged}
					onSelectionChanged={onRowSelectionChanged}
					onRangeSelectionChanged={onRangeSelectionChanged}
					defaultColDef={DEFAULT_SPOTIFY_COL_DEF}
					loadingCellRenderer={LoadingCellRenderer}
					undoRedoCellEditing
					onCellClicked={onCellClicked}
					rowClassRules={superGridRowClassRules}
					icons={SPOTIFY_SUPERGRID_ICONS}
					context={{
						...superGridContext,
						breakdown: selectedBreakdown,
					}}
					getRowId={(rowParams) => `${rowParams.data?.id}_${rowParams.data?.level}`}
					onModelUpdated={(e) => {
						updateDeepestVisibleLevel(e.api);
						supergridProps?.onModelUpdated?.(e);
					}}
					suppressDragLeaveHidesColumns
				/>
			</div>
		</div>
	);
};

export default SpotifyViewSuperGrid;
